import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, CheckCircle2 } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import nammaCetImg from '../assets/nammacet.jpg';

export default function NammaCET() {
  return (
    <div className="min-h-screen bg-orange-50/30 text-slate-900 overflow-hidden relative">
      <div className="relative z-50">
        <Navbar forceOpaque={true} />
      </div>

      <div className="relative pt-32 pb-20 px-6 lg:px-16 min-h-screen flex items-center">
        {/* Background elements */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-orange-200/40 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-amber-200/40 rounded-full blur-[150px] pointer-events-none" />

        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center z-10">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-700 text-sm font-semibold mb-8">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
              AI Rank Predictor & College Allocation
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-extrabold mb-6 font-syne leading-[1.15] text-slate-900">
              Navigate your <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">Academic Future</span>
            </h1>

            <p className="text-slate-600 text-lg lg:text-xl mb-10 max-w-xl font-inter leading-relaxed">
              Your one-stop destination for accurate CET, NEET, and JEE rank predictions. Discover your best-fit colleges and analyze cut-off trends with our advanced AI tools.
            </p>

            <div className="space-y-4 mb-10">
              {['Accurate rank predictions based on past data', 'Personalized college recommendations', 'Real-time cut-off score analysis', 'Comprehensive student community forums'].map((feature, i) => (
                <div key={i} className="flex items-center gap-3 text-slate-700 font-inter">
                  <CheckCircle2 className="text-orange-500" size={20} />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <a href="https://nammacet.com" target="_blank" rel="noopener noreferrer">
                <button className="px-8 py-4 rounded-full bg-orange-500 text-white font-bold font-syne hover:bg-orange-600 transition-colors shadow-lg hover:shadow-orange-500/25">
                  Predict My Rank
                </button>
              </a>
              <a href="https://www.instagram.com/nammacet/" target="_blank" rel="noopener noreferrer">
                <button className="px-8 py-4 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors font-syne font-semibold flex items-center gap-2">
                  Join Community <ExternalLink size={18} />
                </button>
              </a>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200/50 bg-white">
              {/* Fake browser header */}
              <div className="bg-slate-100 px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="mx-auto bg-white rounded-md px-4 py-1 text-xs text-slate-400 font-inter border border-slate-200 w-1/2 text-center truncate">
                  nammacet.com
                </div>
              </div>
              <img src={nammaCetImg} alt="Namma CET Dashboard" className="w-full h-auto object-cover" />
            </div>
            
            <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[radial-gradient(circle,rgba(249,115,22,0.2)_2px,transparent_2px)] bg-[length:12px_12px] opacity-60"></div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
